import type { ListEmployeesFilterInput } from '@/common/graphql';
import { type EmployeeSearchFilterType, UserEmployee, type UserEmployeeType } from '@/common/types';
import { toIdArray } from '@/common/utils';

import type { EmployeeSearchResultRow } from './EmployeeSearchTable';

export const intersectStatuses = (
  selected: EmployeeSearchFilterType['statuses'],
  base: ListEmployeesFilterInput['statuses'],
) => {
  if (!base || base.length === 0) return selected.length === 0 ? undefined : selected;
  if (selected.length === 0) return base;
  return selected.filter(status => base.includes(status));
};

/**
 * Derives the popup's initial filter state (and the state restored by "clear filters") from
 * the calling field's `baseVariables`. Any dimension `baseVariables` already restricts (e.g. a
 * field scoped to a single Company) is pre-selected, matching what `createAllowlistFilter`
 * already limits the corresponding dropdown to - so the UI reflects the enforced constraint
 * from the moment the popup opens, instead of showing an empty selection that silently narrows
 * results without visual indication.
 */
export const deriveInitialFilters = (baseVariables: ListEmployeesFilterInput): EmployeeSearchFilterType => ({
  keyword: '',
  statuses: baseVariables.statuses ?? [],
  levels: baseVariables.levels ?? [],
  companyIds: baseVariables.companyIds ?? [],
  countryIds: baseVariables.countryIds ?? [],
  businessUnitIds: baseVariables.businessUnitIds ?? [],
  departmentIds: baseVariables.departmentIds ?? [],
  jobIds: baseVariables.jobIds ?? [],
});

export const buildEmployeeSearchFilter = (
  baseVariables: ListEmployeesFilterInput,
  filters: EmployeeSearchFilterType,
): ListEmployeesFilterInput => ({
  ...baseVariables,
  keyword: filters.keyword || undefined,
  statuses: intersectStatuses(filters.statuses, baseVariables.statuses),
  levels: filters.levels.length > 0 ? filters.levels : baseVariables.levels,
  companyIds: filters.companyIds.length > 0 ? filters.companyIds : baseVariables.companyIds,
  countryIds: filters.countryIds.length > 0 ? filters.countryIds : baseVariables.countryIds,
  businessUnitIds: filters.businessUnitIds.length > 0 ? filters.businessUnitIds : baseVariables.businessUnitIds,
  departmentIds: filters.departmentIds.length > 0 ? filters.departmentIds : baseVariables.departmentIds,
  jobIds: filters.jobIds.length > 0 ? filters.jobIds : baseVariables.jobIds,
});

/**
 * Merges already-selected employee id(s) (`value`) into `baseVariables.includeIds`, deduplicated
 * and with any id present in `baseVariables.excludeIds` removed. Used to force already-assigned
 * employees into the popup's own query result set (research.md #10, FR-013), so the initial
 * `TableSelection` can be seeded from real, fully-populated rows instead of a lossily-reconstructed
 * one built from the caller's own (differently-shaped) data.
 */
export const mergeIncludeIds = (
  value: string | string[] | null | undefined,
  baseVariables: Pick<ListEmployeesFilterInput, 'includeIds' | 'excludeIds'>,
): string[] | undefined => {
  const valueIds = toIdArray(value);
  const excludeIds = baseVariables.excludeIds ?? [];
  const merged = [...new Set([...(baseVariables.includeIds ?? []), ...valueIds])].filter(
    id => !excludeIds.includes(id),
  );
  return merged.length > 0 ? merged : undefined;
};

/**
 * Converts a selected `EmployeeSearchPopup` result row into the `UserEmployeeType` shape used by
 * the Company/Employee Access permission form (add and edit employee variants alike).
 */
export const toUserEmployee = (row: EmployeeSearchResultRow): UserEmployeeType =>
  UserEmployee.parse({
    id: row.id,
    preferredFullName: row.generalInformation.preferredFullName,
    legalName: row.generalInformation.legalFullName,
    company: row.currentMainContract?.company.name,
    businessUnit: row.currentPosition?.businessUnit.name,
    department: row.currentPosition?.department.name,
  });

/**
 * Builds a `SelectMultiple`/`SelectSingle` `filter` predicate that only allows items whose
 * `value` is present in `allowed`. When `allowed` is empty/undefined, every item passes -
 * i.e. an unrestricted `baseVariables` field imposes no filter-dropdown restriction.
 */
export const createAllowlistFilter =
  <TValue extends string>(allowed: TValue[] | null | undefined) =>
  (item: { value: TValue }): boolean =>
    !allowed || allowed.length === 0 || allowed.includes(item.value);

/**
 * Exact (case-sensitive, non-substring) name match used to auto-select a Department once the
 * popup's own Department options finish loading. Returns `undefined` (no-op) when nothing matches.
 */
export const findDepartmentByExactName = (
  items: Array<{ id: string; name: string }>,
  name: string,
): string | undefined => items.find(item => item.name === name)?.id;

/**
 * Plural counterpart of `findDepartmentByExactName` - resolves `defaultDepartmentName` when it is
 * an array (e.g. `EmployeeSelectMultiple`), matching each name independently. Any name with no
 * match is silently dropped rather than blocking the others.
 */
export const findDepartmentsByExactNames = (items: Array<{ id: string; name: string }>, names: string[]): string[] =>
  names.map(name => findDepartmentByExactName(items, name)).filter((id): id is string => id !== undefined);
